import { ENV } from '../../config/runtimeEnv';
import {
	Chromosome,
	GeneTypeDistribution,
	ChromosomeNameDistribution
} from '../../types/geneCodes/ChromosomeTypes';
import { http } from '../../lib/http/client';

const API_BASE_URL = ENV.VITE_LABIQ_SERVER || '';

// Helper for common API headers

/**
 * Fetch chromosome records with pagination and filters
 */
export const fetchChromosomeRecords = async (
	page: number = 1,
	perPage: number = 30,
	sortBy: string = 'chrom_id',
	sortOrder: string = 'asc',
	filters: {
		gene?: string;
		chromosome_name?: string;
		phenotype?: string;
		gene_type?: string;
		search?: string;
	} = {}
): Promise<{
	data: Chromosome[];
	total: number;
}> => {
	// Remove empty string filters
	const cleanedFilters = Object.fromEntries(
		Object.entries(filters).filter(([_, value]) => value !== "")
	);

	const response = await http.get(`${API_BASE_URL}/chromosome`, {
		params: {
			page,
			per_page: perPage,
			sort_by: sortBy,
			sort_order: sortOrder,
			...cleanedFilters
		}
	});

	return {
		data: response.data.data,
		total: response.data.total
	};
};

/**
 * Fetch a single chromosome record by ID
 */
export const fetchChromosomeRecord = async (id: number): Promise<Chromosome> => {
	const response = await http.get(`${API_BASE_URL}/chromosome/${id}`);

	return response.data.data;
};

/**
 * Create a new chromosome record
 */
export const createChromosomeRecord = async (chromosome: Partial<Chromosome>): Promise<Chromosome> => {
	const response = await http.post(
		`${API_BASE_URL}/chromosome`,
		chromosome
	);

	return response.data.data;
};

/**
 * Update an existing chromosome record
 */
export const updateChromosomeRecord = async (id: number, chromosome: Partial<Chromosome>): Promise<Chromosome> => {
	const response = await http.put(
		`${API_BASE_URL}/chromosome/${id}`,
		chromosome
	);

	return response.data.data;
};

/**
 * Delete a chromosome record
 */
export const deleteChromosomeRecord = async (id: number): Promise<void> => {
	await http.delete(`${API_BASE_URL}/chromosome/${id}`);
};

/**
 * Get chromosome records by gene name
 */
export const getChromosomeByGene = async (
	gene: string,
	page: number = 1,
	perPage: number = 30,
	sortBy: string = 'chrom_id',
	sortOrder: string = 'asc'
): Promise<{
	data: Chromosome[];
	total: number;
	gene: string;
}> => {
	const response = await http.get(`${API_BASE_URL}/chromosome/gene/${gene}`, {
		params: {
			page,
			per_page: perPage,
			sort_by: sortBy,
			sort_order: sortOrder
		}
	});

	return {
		data: response.data.data,
		total: response.data.total,
		gene: response.data.gene
	};
};

/**
 * Get chromosome records by phenotype
 */
export const getChromosomeByPhenotype = async (
	phenotype: string,
	page: number = 1,
	perPage: number = 30,
	sortBy: string = 'chrom_id',
	sortOrder: string = 'asc'
): Promise<{
	data: Chromosome[];
	total: number;
	phenotype: string;
}> => {
	const response = await http.get(`${API_BASE_URL}/chromosome/phenotype/${phenotype}`, {
		params: {
			page,
			per_page: perPage,
			sort_by: sortBy,
			sort_order: sortOrder
		}
	});

	return {
		data: response.data.data,
		total: response.data.total,
		phenotype: response.data.phenotype
	};
};

/**
 * Get chromosome records by gene type
 */
export const getChromosomeByGeneType = async (
	geneType: string,
	page: number = 1,
	perPage: number = 30,
	sortBy: string = 'chrom_id',
	sortOrder: string = 'asc'
): Promise<{
	data: Chromosome[];
	total: number;
	gene_type: string;
}> => {
	const response = await http.get(`${API_BASE_URL}/chromosome/gene-type/${geneType}`, {
		params: {
			page,
			per_page: perPage,
			sort_by: sortBy,
			sort_order: sortOrder
		}
	});

	return {
		data: response.data.data,
		total: response.data.total,
		gene_type: response.data.gene_type
	};
};

/**
 * Get gene type distribution from chromosome
 */
export const getGeneTypeDistribution = async (): Promise<GeneTypeDistribution[]> => {
	const response = await http.get(`${API_BASE_URL}/chromosome/distribution/gene-type`);

	return response.data.data;
};

/**
 * Get chromosome name distribution
 */
export const getChromosomeNameDistribution = async (): Promise<ChromosomeNameDistribution[]> => {
	const response = await http.get(`${API_BASE_URL}/chromosome/distribution/chromosome-name`);

	return response.data.data;
};